import { Pressable, StyleSheet, View } from 'react-native';

import { colors, fontSizes, radii, spacing } from '@/constants/theme';
import {
  ACTION_STATUS,
  UNRESOLVED_STATUSES,
  type Action,
  type ActionStatus,
  type Deal,
} from '@/types/deal';

import { DealAvailabilityBar } from './AvailabilityBar';
import { Avatar } from './Avatar';
import { BrandLogo } from './BrandLogo';
import { Pill, type PillTone } from './Pill';
import { Text } from './Text';

// Distinct labels per status — athletes care about the difference
// between "they saw it" (clicked) and "money's in" (paid). The Record
// type forces every ACTION_STATUS value to have an entry.
//
// Clicked gets no pill. Every card in the IN PROGRESS section sits at that
// status, so a "Started" pill repeated the section header on every row and
// told the reader nothing. A pill comes back the moment something actually
// happened: Verifying, Verified, Paid, Rejected, Expired.
const STATUS_PILL: Record<ActionStatus, { label: string; tone: PillTone } | null> = {
  [ACTION_STATUS.Clicked]: null,
  [ACTION_STATUS.Pending]: { label: 'Verifying', tone: 'blue' },
  [ACTION_STATUS.Verified]: { label: 'Verified', tone: 'greenSoft' },
  [ACTION_STATUS.Paid]: { label: 'Paid', tone: 'green' },
  [ACTION_STATUS.Rejected]: { label: 'Rejected', tone: 'coral' },
  [ACTION_STATUS.Expired]: { label: 'Expired', tone: 'warn' },
};

const NEW_PILL: { label: string; tone: PillTone } = { label: 'Start', tone: 'outline' };

export type DealRowProps = {
  deal: Deal;
  action?: Action;
  onPress: () => void;
  testID?: string;
};

/**
 * The single card shape for every Deals-tab section — IN PROGRESS, EARN NEXT,
 * and COMPLETED. A resolved deal renders the same card with its outcome pill
 * ("Paid", "Rejected") in place of "Start"; there is no separate compact
 * receipt row. `action` is absent only for a deal the user hasn't started.
 *
 * The one thing a resolved card drops is cap state — the availability bar and
 * the full-deal dim both answer "can I still get in?", which is settled once
 * the deal resolved.
 */
export function DealRow({ deal, action, onPress, testID }: DealRowProps) {
  const pill = action ? STATUS_PILL[action.status] : NEW_PILL;
  // VoiceOver still gets a status for the pill-less Clicked card.
  const statusLabel = pill?.label ?? 'In progress';
  const initials = deal.brand_name.slice(0, 2).toUpperCase();
  // Same predicate the store uses to fill its COMPLETED bucket
  // (deals.store.ts `sections`), off the same shared constant so the card and
  // the section it sits in can't drift apart.
  const resolved = !!action && !UNRESOLVED_STATUSES.includes(action.status);
  // Cap state describes whether you can still get IN. Once you're resolved
  // that question is answered, and the "closed for now" dim would otherwise
  // gray out a deal you were already paid for.
  const dimmed = deal.full && !resolved;

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.card,
        dimmed && styles.cardFull,
        // Skip the press-brighten on a dimmed card — it's already at 0.8, and
        // 0.85 would brighten it on press instead of dimming.
        pressed && !dimmed && styles.cardPressed,
      ]}
      accessibilityRole="button"
      // Pill label is part of the label, not a child element: Pressable makes
      // the whole card one VoiceOver node, so the Pill's own text is never
      // announced. Without it all three sections read identically and a paid
      // deal is indistinguishable from an unstarted one.
      accessibilityLabel={`${deal.display_name} — ${deal.title}, ${statusLabel}`}
      testID={testID}
    >
      <View style={styles.topRow}>
        <View style={styles.logoSlot}>
          <BrandLogo
            url={deal.brand_logo_url}
            size={48}
            fallback={<Avatar initials={initials} size={48} tone="green" />}
          />
        </View>
        <View style={styles.body}>
          {/* Status rides on the brand line rather than a row of its own. The
              payout figure used to anchor the left of that row with the pill
              pinned right; with the figure gone a lone pill was stranded in a
              full-width row, burning vertical space and leaving a dead gutter. */}
          <View style={styles.brandRow}>
            <Text variant="captionBold" color="textMuted" uppercase style={styles.brand} numberOfLines={1}>
              {deal.display_name}
            </Text>
            {pill && <Pill tone={pill.tone}>{pill.label}</Pill>}
          </View>
          {/* No MoneyDisplay: the title already leads with the amount, and a
              second figure beside it contradicted itself where the two differ
              — SoFi checking reads "Get $50 for opening a SoFi account" over a
              $20 user cut ($50 = $20 from us + $30 from SoFi). Matches the
              same call already made in FeaturedDealCard and DealHeroCard. */}
          <Text variant="bodyBold" color="deepGreen" style={styles.title} numberOfLines={2}>
            {deal.title}
          </Text>
        </View>
      </View>
      {/* Full-width below the logo+title so "N left · of M this month · badge"
          gets the whole card width and never wraps (it's cramped beside the
          logo column). An uncapped deal gets the same-height "No monthly
          limit" bar, so every card in the section is one height.

          Hidden once resolved: on a full deal the bar renders a Lock "Full"
          pill and "0 left · reopens Sep 1", and on a low one an orange Zap
          "Almost gone" — scarcity pressure aimed at a deal the user has
          already finished. Someone whose own conversion took the last spot
          would see their paid deal wearing a padlock. */}
      {!resolved && <DealAvailabilityBar deal={deal} compact style={styles.bar} />}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: spacing.base,
    backgroundColor: colors.surface,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.border,
  },
  topRow: {
    flexDirection: 'row',
    gap: spacing.base,
  },
  cardPressed: {
    opacity: 0.85,
  },
  // A full deal reads as closed-for-now — dimmed but still tappable so the
  // user can open it and see when it reopens.
  cardFull: {
    opacity: 0.8,
    backgroundColor: colors.background,
  },
  logoSlot: {
    width: 48,
    height: 48,
  },
  body: {
    flex: 1,
    gap: spacing.xs,
  },
  title: {
    fontSize: fontSizes.base,
    lineHeight: fontSizes.base * 1.3,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  // Brand takes the slack so the pill keeps its content width and a long
  // name ("CAPITAL ONE SHOPPING") truncates instead of shoving the pill off.
  brand: {
    flex: 1,
  },
  bar: {
    marginTop: spacing.sm,
  },
});
